import React from 'react';
import { Trophy, Handshake } from 'lucide-react';
import { colors } from '../../constants/theme';

const STATUS_STYLE = {
  finished: { border: '#10B981', background: '#F0FDF4', badge: 'bg-green-100 text-green-800', label: 'Finalizado' },
  in_progress: { border: '#F59E0B', background: '#FFFBEB', badge: 'bg-yellow-100 text-yellow-800', label: 'Em Andamento' },
  default: { border: '#6B7280', background: '#F9FAFB', badge: 'bg-gray-100 text-gray-800', label: 'Aguardando' },
};

/**
 * Card de confronto usado nas eliminatórias e no histórico dos grupos.
 *
 * Aceita placar em qualquer um dos formatos que a API devolve: `finalScore` /
 * `currentScore` (chaveamento) ou `goalsPlayer1` / `goalsPlayer2` (grupos).
 *
 * `allowDraw` liga o rótulo de empate — na fase de grupos empatar é resultado
 * válido; no mata-mata, não.
 */
export default function ConfrontationCard({
  match,
  title,
  winnerLabel = 'Vencedor',
  allowDraw = false,
}) {
  const style = STATUS_STYLE[match.status] || STATUS_STYLE.default;

  const scorePlayer1 =
    match.finalScore?.player1 ?? match.currentScore?.player1 ?? match.goalsPlayer1;
  const scorePlayer2 =
    match.finalScore?.player2 ?? match.currentScore?.player2 ?? match.goalsPlayer2;

  // Mantém o comportamento do chaveamento: só 'pending' esconde o placar;
  // uma partida pronta mostra "-" nos dois lados.
  const showScore = match.status !== 'pending';
  const isFinished = match.status === 'finished';
  const isDraw = isFinished && !match.winnerId;

  const sideClass = (playerId) =>
    isFinished && match.winnerId === playerId
      ? 'bg-green-200 border-2 border-green-400'
      : 'bg-gray-50';

  return (
    <div
      className="border-2 rounded-lg p-4"
      style={{ borderColor: style.border, backgroundColor: style.background }}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <h4 className="font-semibold text-lg">{title}</h4>
        <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${style.badge}`}>
          {style.label}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <div className={`text-center flex-1 p-3 rounded ${sideClass(match.player1?.id)}`}>
          <div className="font-semibold">{match.player1?.name}</div>
          {match.player1?.category && (
            <div className="text-sm text-gray-600">Categoria {match.player1.category}</div>
          )}
          {showScore && (
            <div className="text-2xl font-bold mt-2" style={{ color: colors.primary }}>
              {scorePlayer1 ?? '-'}
            </div>
          )}
        </div>

        <div className="mx-4 text-center">
          <div className="text-xl font-bold text-gray-500">VS</div>
        </div>

        <div className={`text-center flex-1 p-3 rounded ${sideClass(match.player2?.id)}`}>
          <div className="font-semibold">{match.player2?.name}</div>
          {match.player2?.category && (
            <div className="text-sm text-gray-600">Categoria {match.player2.category}</div>
          )}
          {showScore && (
            <div className="text-2xl font-bold mt-2" style={{ color: colors.primary }}>
              {scorePlayer2 ?? '-'}
            </div>
          )}
        </div>
      </div>

      {isFinished && match.winnerId && (
        <div className="mt-3 text-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
            <Trophy className="h-4 w-4 mr-1" />
            {winnerLabel}:{' '}
            {match.winnerId === match.player1?.id ? match.player1?.name : match.player2?.name}
          </span>
        </div>
      )}

      {isDraw && allowDraw && (
        <div className="mt-3 text-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700">
            <Handshake className="h-4 w-4 mr-1" />
            Empate
          </span>
        </div>
      )}
    </div>
  );
}
